import React, { useState, useEffect, useCallback } from 'react';
import { numbersData, LanguageCode, languageNames, bcp47Tags } from '../data/numbers';
import { speakWord } from '../utils/audio';
import './NumbersModule.scss';

const NumbersModule: React.FC = () => {
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>('en');
  const [currentNumberIndex, setCurrentNumberIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);

  const currentNumbers = numbersData[currentLanguage];
  const currentItem = currentNumbers[currentNumberIndex];

  const handleNumberClick = (index: number) => {
    setCurrentNumberIndex(index);
    const item = currentNumbers[index];
    speakWord(item.native, bcp47Tags[currentLanguage]);
  };

  const nextNumber = useCallback(() => {
    setCurrentNumberIndex((prev) => (prev + 1) % currentNumbers.length);
  }, [currentNumbers.length]);

  const prevNumber = () => {
    setCurrentNumberIndex((prev) => (prev - 1 + currentNumbers.length) % currentNumbers.length);
  };

  useEffect(() => {
    if (isAutoPlaying) {
      const timer = setTimeout(() => {
        nextNumber();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isAutoPlaying, currentNumberIndex, nextNumber]);

  useEffect(() => {
    // Speak when changing via navigation
    const item = currentNumbers[currentNumberIndex];
    speakWord(item.native, bcp47Tags[currentLanguage]);
  }, [currentNumberIndex, currentLanguage, currentNumbers]);

  return (
    <div className="numbers-module">
      <div className="module-controls">
        <div className="language-selector">
          <label htmlFor="num-lang">Language: </label>
          <select
            id="num-lang"
            value={currentLanguage}
            onChange={(e) => {
              setCurrentLanguage(e.target.value as LanguageCode);
              setCurrentNumberIndex(0);
            }}
          >
            {(Object.keys(languageNames) as LanguageCode[]).map(code => (
              <option key={code} value={code}>{languageNames[code]}</option>
            ))}
          </select>
        </div>
        <button
          className={`autoplay-btn ${isAutoPlaying ? 'active' : ''}`}
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
        >
          {isAutoPlaying ? '⏹ Stop Autoplay' : '▶️ Autoplay'}
        </button>
      </div>

      <div className="display-area">
        <div className="number-card-large">
          <div className="number-visual">
            <span className="big-digit">{currentItem.number}</span>
            <span className="big-emoji">{currentItem.image}</span>
          </div>
          <div className="number-words">
            <h2 className="native-word">{currentItem.native}</h2>
            {currentLanguage !== 'en' && <p className="phonetic-word">{currentItem.word}</p>}
          </div>
          <button className="speak-btn" onClick={() => speakWord(currentItem.native, bcp47Tags[currentLanguage])}>
            🔊 Listen
          </button>
        </div>

        <div className="navigation">
          <button onClick={prevNumber} className="nav-btn">⬅️ Previous</button>
          <button onClick={nextNumber} className="nav-btn">Next ➡️</button>
        </div>
      </div>

      <div className="number-grid-mini">
        {currentNumbers.map((item, idx) => (
          <button
            key={item.number}
            className={`grid-item ${idx === currentNumberIndex ? 'active' : ''}`}
            onClick={() => handleNumberClick(idx)}
          >
            {item.number}
          </button>
        ))}
      </div>
    </div>
  );
};

export default NumbersModule;
